import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { ErrorBoundary } from '@/components/layout/ErrorBoundary'
import { HomePage } from '@/features/home/HomePage'
import { CatalogPage } from '@/features/catalog/CatalogPage'
import { ProductListPage } from '@/features/products/ProductListPage'
import { ProductCreatePage } from '@/features/products/ProductCreatePage'
import { ProductDetailPage } from '@/features/products/ProductDetailPage'
import { ProductChangeCodePage } from '@/features/products/ProductChangeCodePage'
import { SuggestionsPage } from '@/features/proto/SuggestionsPage'
import { PrListPage, PoListPage, PoDetailPage } from '@/features/proto/PoPages'
import { PoCreatePageNew } from '@/features/purchasing/PoCreatePageNew'
import { PoCreateFromBomPage } from '@/features/purchasing/PoCreateFromBomPage'
import { ProductionExecutionPage } from '@/features/productionExecution/ProductionExecutionPage'
import { PoPrintPage } from '@/features/proto/PoPrintView'
import { SupplierListPage } from '@/features/suppliers/SupplierListPage'
import { SupplierFormPage } from '@/features/suppliers/SupplierFormPage'
import { SupplierDetailPage } from '@/features/suppliers/SupplierDetailPage'
import { MaterialListPage } from '@/features/materials/MaterialListPage'
import { MaterialFormPage } from '@/features/materials/MaterialFormPage'
import { MaterialDetailPage } from '@/features/materials/MaterialDetailPage'
import { CustomerListPage } from '@/features/customers/CustomerListPage'
import { CustomerFormPage } from '@/features/customers/CustomerFormPage'
import { CustomerDetailPage } from '@/features/customers/CustomerDetailPage'
import { SalesPlanningSpreadsheetPage } from '@/features/salesPlanning/SalesPlanningSpreadsheetPage'
import { SalesOrderCreatePage } from '@/features/salesOrders/SalesOrderCreatePage'
import { SalesOrderDetailPage } from '@/features/salesOrders/SalesOrderDetailPage'
import { BomListPage } from '@/features/boms/BomListPage'
import { BomFormPage } from '@/features/boms/BomFormPage'
import { BomDetailPage } from '@/features/boms/BomDetailPage'
import { BomIntakePage } from '@/features/boms/BomIntakePage'
import { BomReviewNamesPage } from '@/features/boms/BomReviewNamesPage'
import { StandardNamesPage } from '@/features/standardNames/StandardNamesPage'
import { DataStandardizationHubPage } from '@/features/dataStandardization/DataStandardizationHubPage'
import { ProductConditionMatrixPage } from '@/features/boms/ProductConditionMatrixPage'
import { MasterBomComposerPage } from '@/features/boms/master/MasterBomComposerPage'
import { MasterBomComparePage } from '@/features/boms/master/MasterBomComparePage'
import { BomAggregatePage } from '@/features/boms/aggregate/BomAggregatePage'
import { PackagingBomListPage } from '@/features/packagingBoms/PackagingBomListPage'
import { PackagingBomDetailPage } from '@/features/packagingBoms/PackagingBomDetailPage'
import { DemandListPage } from '@/features/demand/DemandListPage'
import { DemandFormPage } from '@/features/demand/DemandFormPage'
import { DemandDetailPage } from '@/features/demand/DemandDetailPage'
import { ExplosionRunListPage } from '@/features/explosions/ExplosionRunListPage'
import { ExplosionRunDetailPage } from '@/features/explosions/ExplosionRunDetailPage'
import {
  ProductionOrdersPage,
  ProductionOrdersPrintPage,
} from '@/features/productionOrders'
import { ProductionOutputRecordPage } from '@/features/productionOutput/ProductionOutputRecordPage'
import { ProductionProgressPage } from '@/features/productionOutput/ProductionProgressPage'
import { MrpPlanningDashboardPage } from '@/features/planning/mrp'
import { StockDetailPage } from '@/features/proto/StockDetailPage'
import { StockPage } from '@/features/warehouse/StockPage'
import { ReceiptsPage } from '@/features/warehouse/ReceiptsPage'
import { IssuesPage } from '@/features/warehouse/IssuesPage'
import { ReservationsPage } from '@/features/warehouse/ReservationsPage'
import { TransactionsPage } from '@/features/warehouse/TransactionsPage'
import { StocktakeHistoryPage } from '@/features/warehouse/StocktakeHistoryPage'
import { IssueSlipsPage } from '@/features/proto/WarehousePages'
import { ReportsHubPage } from '@/features/reports/ReportsHubPage'
import { InventoryDashboardPage } from '@/features/reports/inventoryDashboard/InventoryDashboardPage';
import { OpenPurchaseOrdersReportPage } from '@/features/reports/OpenPurchaseOrdersReportPage'
import { ProductionOutputReportPage } from '@/features/reports/ProductionOutputReportPage'
import { PackagingPricingReportPage } from '@/features/reports/PackagingPricingReportPage'
import { ProductBomCostReportPage } from '@/features/reports/ProductBomCostReportPage'
import { AuthProvider } from '@/features/auth/AuthContext'
import { AuthGuard } from '@/features/auth/AuthGuard'
import { LoginPage } from '@/features/auth/LoginPage'
import { AdminUsersPage } from '@/features/admin/AdminUsersPage'

function RootLayout() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </ErrorBoundary>
  )
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<AuthGuard />}>
        <Route element={<AppShell />}>
          <Route index element={<HomePage />} />
          <Route path="sales" element={<Navigate to="/sales/orders" replace />} />
          <Route path="sales/orders" handle={{ layout: 'workspace' }} element={<SalesPlanningSpreadsheetPage />} />
          <Route path="sales/orders/new" element={<SalesOrderCreatePage />} />
          <Route path="sales/orders/:id" element={<SalesOrderDetailPage />} />
          <Route path="sales/customers" element={<CustomerListPage />} />
          <Route path="sales/customers/new" element={<CustomerFormPage />} />
          <Route path="sales/customers/:id" element={<CustomerDetailPage />} />
          <Route path="products" element={<ProductListPage />} />
          <Route path="products/new" element={<ProductCreatePage />} />
          <Route
            path="products/:productId/change-code"
            element={<ProductChangeCodePage />}
          />
          <Route path="products/:productId" element={<ProductDetailPage />} />
          <Route
            path="products/:productId/matrix"
            element={<ProductConditionMatrixPage />}
          />
          <Route
            path="products/:productId/:folder"
            element={<ProductDetailPage />}
          />
          <Route path="catalog" element={<CatalogPage />} />
          <Route path="catalog/bom-aggregate" handle={{ layout: 'workspace' }} element={<BomAggregatePage />} />
          <Route
            path="planning"
            element={<Navigate to="/planning/production-orders" replace />}
          />
          <Route
            path="planning/production-orders"
            handle={{ layout: 'workspace' }}
            element={<SalesPlanningSpreadsheetPage />}
          />
          <Route path="planning/production-orders/legacy" handle={{ layout: 'workspace' }} element={<ProductionOrdersPage />} />
          <Route
            path="planning/production-orders/:id/execution"
            element={<ProductionExecutionPage />}
          />
          <Route
            path="planning/production-orders/print"
            element={<ProductionOrdersPrintPage />}
          />
          <Route
            path="planning/mrp"
            element={<MrpPlanningDashboardPage />}
          />
          <Route
            path="production/record"
            element={<ProductionOutputRecordPage />}
          />
          <Route
            path="production/progress"
            handle={{ layout: 'workspace' }}
            element={<ProductionProgressPage />}
          />
          <Route
            path="production/progress/:planLineId"
            handle={{ layout: 'workspace' }}
            element={<ProductionProgressPage />}
          />
          <Route
            path="production"
            element={<Navigate to="/planning/production-orders" replace />}
          />
          <Route path="reports" element={<ReportsHubPage />} />
          <Route path="reports/inventory-dashboard" element={<InventoryDashboardPage />} />
          <Route
            path="reports/open-purchase-orders"
            element={<OpenPurchaseOrdersReportPage />}
          />
          <Route
            path="reports/production-output"
            element={<ProductionOutputReportPage />}
          />
          <Route
            path="reports/packaging-pricing"
            element={<PackagingPricingReportPage />}
          />
          <Route
            path="reports/product-bom-costs"
            element={<ProductBomCostReportPage />}
          />

          <Route path="purchasing" element={<PoListPage />} />
          <Route path="purchasing/suggestions" element={<SuggestionsPage />} />
          <Route path="purchasing/pr" element={<PrListPage />} />
          <Route path="purchasing/po" element={<PoListPage />} />
          <Route path="purchasing/po/new" element={<PoCreatePageNew />} handle={{ layout: 'workspace' }} />
          <Route path="purchasing/po/new-from-bom" element={<PoCreateFromBomPage />} handle={{ layout: 'workspace' }} />
          <Route path="purchasing/po/new-free" element={<Navigate to="/purchasing/po/new?free=1" replace />} />
          <Route path="purchasing/pricing-workbooks" element={<Navigate to="/purchasing/suppliers" replace />} />
          <Route path="purchasing/po/:poId/edit" element={<PoCreatePageNew />} handle={{ layout: 'workspace' }} />
          <Route path="purchasing/po/:poId/print" element={<PoPrintPage />} />
          <Route path="purchasing/po/:poId" element={<PoDetailPage />} handle={{ layout: 'workspace' }} />
          <Route path="purchasing/suppliers" element={<SupplierListPage />} />
          <Route path="purchasing/suppliers/new" element={<SupplierFormPage />} />
          <Route
            path="purchasing/suppliers/:supplierId/edit"
            element={<SupplierFormPage />}
          />
          <Route
            path="purchasing/suppliers/:supplierId"
            element={<SupplierDetailPage />}
          />
          <Route path="purchasing/materials" element={<MaterialListPage />} />
          <Route path="purchasing/materials/new" element={<MaterialFormPage />} />
          <Route
            path="purchasing/materials/:materialId"
            element={<MaterialDetailPage />}
          />
          <Route path="purchasing/boms" element={<BomListPage />} />
          <Route path="purchasing/boms/new" element={<BomFormPage />} />
          <Route path="purchasing/boms/intake" element={<BomIntakePage />} />
          <Route path="purchasing/boms/review-names" element={<BomReviewNamesPage />} />
          <Route path="boms/review-names" element={<BomReviewNamesPage />} />
          <Route path="purchasing/data-standardization" element={<DataStandardizationHubPage />} />
          <Route path="purchasing/standard-names" element={<StandardNamesPage />} />
          <Route path="boms/standard-names" element={<StandardNamesPage />} />
          <Route path="purchasing/boms/:bomId" element={<BomDetailPage />} />
          <Route
            path="purchasing/boms/matrix/:productId"
            element={<ProductConditionMatrixPage />}
          />
          <Route
            path="purchasing/boms/:productId/master"
            element={<MasterBomComposerPage />}
          />
          <Route
            path="purchasing/boms/:productId/compare"
            element={<MasterBomComparePage />}
          />
          <Route
            path="purchasing/packaging-boms"
            element={<PackagingBomListPage />}
          />
          <Route
            path="purchasing/packaging-boms/:id"
            element={<PackagingBomDetailPage />}
          />
          <Route path="purchasing/demand" element={<DemandListPage />} />
          <Route path="purchasing/demand/new" element={<DemandFormPage />} />
          <Route path="purchasing/demand/:id" element={<DemandDetailPage />} />
          <Route path="purchasing/explosions" element={<ExplosionRunListPage />} />
          <Route
            path="purchasing/explosions/:id"
            element={<ExplosionRunDetailPage />}
          />
          <Route path="purchasing/quotes" element={<Navigate to="/purchasing/suppliers" replace />} />
          <Route path="purchasing/quotes/*" element={<Navigate to="/purchasing/suppliers" replace />} />

          <Route
            path="warehouse"
            element={<Navigate to="/warehouse/stock" replace />}
          />
          <Route path="warehouse/stock" handle={{ layout: 'workspace' }} element={<StockPage />} />
          <Route path="warehouse/free-stock" element={<Navigate to="/warehouse/stock?kind=free" replace />} />
          <Route path="warehouse/issue-slips" handle={{ layout: 'workspace' }} element={<IssueSlipsPage />} />
          <Route path="warehouse/stock/:materialId" element={<StockDetailPage />} />
          <Route path="warehouse/receipts" handle={{ layout: 'workspace' }} element={<ReceiptsPage />} />
          <Route path="warehouse/issues" handle={{ layout: 'workspace' }} element={<IssuesPage />} />
          <Route
            path="warehouse/returns"
            element={<Navigate to="/warehouse/issues" replace />}
          />
          <Route path="warehouse/transactions" handle={{ layout: 'workspace' }} element={<TransactionsPage />} />
          <Route path="warehouse/stocktakes" handle={{ layout: 'workspace' }} element={<StocktakeHistoryPage />} />
          <Route path="warehouse/reservations" handle={{ layout: 'workspace' }} element={<ReservationsPage />} />

          <Route path="admin/users" element={<AdminUsersPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Route>,
  ),
)

export function AppRouter() {
  return <RouterProvider router={router} />
}
